import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Calendar, CheckCircle, RotateCcw } from 'lucide-react';
import { isClientCourtCost } from '@/utils/courtCosts';

interface CaseCourtCostsProps {
  caseId: string;
}

const brl = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
const formatDate = (value?: string | null) => (value ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : null);

/** Custas processuais pagas pelo cliente, exibidas só na tela do processo. */
export const CaseCourtCosts = ({ caseId }: CaseCourtCostsProps) => {
  const { toast } = useToast();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data, error } = await supabase
      .from('financial_entries')
      .select('*')
      .eq('case_id', caseId)
      .order('payment_date', { ascending: false });
    if (!error) setEntries((data || []).filter(isClientCourtCost));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [caseId]);

  const setPaid = async (entry: any, paid: boolean) => {
    const today = new Date().toISOString().slice(0, 10);
    const update = paid
      ? { status: 'Pago', payment_confirmed: true, actual_payment_date: today, payment_notes: '[PAGO PELO CLIENTE]' }
      : { status: 'Pendente', payment_confirmed: false, actual_payment_date: null, payment_notes: '[NÃO PAGO PELO CLIENTE]' };
    const { error } = await supabase.from('financial_entries').update(update).eq('id', entry.id);
    if (error) {
      toast({ title: 'Não foi possível atualizar', description: error.message, variant: 'destructive' });
      return;
    }
    setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, ...update } : e)));
  };

  const pending = entries.filter((e) => e.status !== 'Pago');
  const total = entries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const pendingTotal = pending.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Custas processuais</p>
          <p className="mt-1 text-sm text-slate-500">Pagas pelo cliente — não entram no Financeiro do escritório.</p>
        </div>
        {entries.length > 0 && (
          <div className="text-right text-xs text-slate-500">
            <span className="block text-lg font-semibold tabular-nums text-slate-900">{brl(total)}</span>
            {pendingTotal > 0 ? <span className="font-medium text-amber-700">{brl(pendingTotal)} pendentes</span> : 'tudo pago'}
          </div>
        )}
      </div>

      {loading ? (
        <p className="px-5 py-6 text-sm text-slate-400">Carregando custas...</p>
      ) : entries.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-400">Nenhuma custa processual registrada neste processo.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {entries.map((entry) => {
            const paid = entry.status === 'Pago';
            return (
              <div key={entry.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <span className={`h-9 w-1 shrink-0 rounded-full ${paid ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{entry.description}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                    {entry.category && <span>{entry.category}</span>}
                    {(paid ? entry.actual_payment_date : entry.payment_date) && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {paid ? 'Pago em ' : 'Vence '}
                        {formatDate(paid ? entry.actual_payment_date : entry.payment_date)}
                      </span>
                    )}
                  </p>
                </div>
                <span className="text-sm font-semibold tabular-nums text-slate-900">{brl(Number(entry.amount))}</span>
                {paid ? (
                  <button
                    type="button"
                    onClick={() => setPaid(entry, false)}
                    className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-xs font-medium text-emerald-700 hover:bg-slate-100 hover:text-slate-700"
                    title="Marcar como não paga"
                  >
                    <CheckCircle className="h-3.5 w-3.5" /> Paga pelo cliente
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPaid(entry, true)}
                    className="inline-flex h-8 items-center gap-1 rounded-lg border border-emerald-200 px-2.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    <RotateCcw className="h-3.5 w-3.5 rotate-180" /> Marcar como paga
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
